<script setup>
import XlAvatar from '../xl/XlAvatar.vue';
import { longDate } from '../../helpers/usDate';
import XlListRow from '../xl/XlListRow.vue';
import XlTag from '../xl/XlTag.vue';
import IconMail from '../icons/IconMail.vue';
import { computed } from 'vue';
import { lastActive } from '../../helpers/messageTime';

const props = defineProps({
  conversation: { type: Object, required: true },
  // The broker's own name on their own list is noise — every row would say it.
  showBroker: { type: Boolean, default: false },
});

defineEmits(['open']);

const sentOn = computed(() => longDate(props.conversation.last_message_at));

const subtitle = computed(() => {
  const { last_message: text, last_message_files: files } = props.conversation;
  if (text) return text;
  if (files) return files === 1 ? 'Sent a file' : `Sent ${files} files`;

  return text === null || text === undefined ? 'No messages yet' : '';
});
</script>

<template>
  <XlListRow
    class="conversation-row"
    role="button"
    tabindex="0"
    data-testid="conversation-row"
    @click="$emit('open', props.conversation.policyholder_id)"
    @keydown.enter="$emit('open', props.conversation.policyholder_id)"
    @keydown.space.prevent="$emit('open', props.conversation.policyholder_id)"
  >
    <template #leading>
      <XlAvatar :name="props.conversation.name || ''" :src="props.conversation.photo || ''" :color="props.conversation.avatar_color || ''" :size="42" aria-hidden="true" />
    </template>

    <template #title>{{ props.conversation.name || 'Unnamed policyholder' }}</template>
    <template #subtitle>{{ subtitle }}</template>

    <template #trailing>
      <span class="conversation-row__meta">
        <XlTag v-if="props.conversation.waiting" tone="warning" data-testid="waiting-tag">Waiting</XlTag>
        <XlTag v-if="props.conversation.replied_by" tone="brand" data-testid="replied-tag"><IconMail color="currentColor" size="12" /><span class="conversation-row__replied">{{ props.conversation.replied_by }} replied</span></XlTag>
        <span v-if="props.showBroker && props.conversation.broker_name" class="conversation-row__broker" data-testid="row-broker">
          {{ props.conversation.broker_name }}
        </span>
        <time v-if="props.conversation.last_message_at" class="conversation-row__when" data-testid="row-date" :datetime="props.conversation.last_message_at" :title="`Last message ${sentOn}`">{{ lastActive(props.conversation.last_message_at) }}</time>
      </span>
    </template>
  </XlListRow>
</template>

<style scoped lang="scss">
.conversation-row {
  position: relative;
  isolation: isolate;
  cursor: pointer;

  // Drawn wider than the row so the tint clears the avatar and the time
  // without bending the divider.
  &::before {
    content: '';
    position: absolute;
    inset: 0 -12px;
    z-index: -1;
    border-radius: 12px;
  }

  &:hover::before { background: var(--xl-color-border-subtle, #f6f2f8); }

  &:focus-visible {
    outline: 2px solid var(--xl-color-primary, #5e2f78);
    outline-offset: -2px;
  }

  // XlAvatar sets its font size inline, so only !important overrides it.
  :deep(.xl-avatar) { font-size: 13px !important; }

  // Room for the name before the meta wraps under it, never a sliver the name overflows.
  :deep(.xl-list-row__text) { flex: 1 1 120px; }

  :deep(.xl-list-row__text b),
  :deep(.xl-list-row__text span) {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-left: auto;
    min-width: 0;
    max-width: 100%;
  }

  &__meta :deep(.xl-tag) { min-width: 0; }

  &__meta :deep(.xl-tag svg) { flex: none; }

  &__replied {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__broker {
    font-size: 12.5px;
    color: var(--xl-color-outline);
    white-space: nowrap;
  }

  &__when {
    font-size: 12.5px;
    color: var(--xl-color-outline-variant);
    flex: none;
    white-space: nowrap;
  }
}
</style>
